<template>
	<div class="guest-access-list">
		<div
			v-for="list in guestLists"
			:key="list.Id"
		>
			<div
				class="guest-access-list__parent-row flex gap-2"
				:class="[
					{ 'cursor-pointer': list.References.length > 0 },
					{ 'guest-access-list__parent-row--static-hover': list.isContextMenuOpen },
					{
						'guest-access-list__parent-row--link items-start py-3': list.Id === 'link-guests',
						'items-center': list.Id !== 'link-guests'
					}
				]"
				@click="expandList(list, $event)"
			>
				<CIconButton
					class="guest-access-list__chevron text-icon-subtle"
					:class="{ 'guest-access-list__chevron--hidden': list.References.length === 0 }"
					:icon="list.isExpanded ? 'chevron-down' : 'chevron-right'"
					:intense="false"
				/>
				<!-- @vue-ignore data-testid -->
				<CCheckbox
					:value="list.isActive"
					:disabled="list.isDisabled"
					:data-testid="`guest-list-${list.Id}`"
					:aria-label="list.Name"
					@input="guestListChecked(list, $event)"
					@change="onGuestListCheckboxUserChange(list.Id, $event)"
				/>
				<span
					class="guest-access-list__row flex-1 flex gap-3"
					:class="[ list.Id === 'link-guests' ? 'guest-access-list__row--link-guest items-start py-0' : 'items-center py-2' ]"
				>
					<Inline :svg="list.icon" class="text-icon-subtle ml-2" />
					<span
						class="min-w-0"
						:class="{ 'truncate': list.Id !== 'link-guests' }"
						data-tooltip
					>
						{{ list.Name }}
					</span>
					<CBadge :text="getBadgeText(list)" />
					<CToolTip
						v-if="list.CanEditProfile && list.isActive"
						placement="top"
					>
						<template #button>
							<Inline svg="edit-profile" class="text-icon-subtle flex" />
						</template>
						<span>Guests can edit their profile</span>
					</CToolTip>
					<CToolTip
						v-if="list.CanRegister && list.isActive"
						placement="top"
					>
						<template #button>
							<Inline svg="register" class="text-icon-subtle flex" />
						</template>
						<span>Guests can register on this form</span>
					</CToolTip>
				</span>
				<span
					v-if="list.folderName"
					class="guest-access-list__parent-row--folder flex items-center gap-2 ml-auto shrink-0 b9:show"
				>
					<Inline svg="folder" class="text-icon-subtle" />
					<span class="text-helptext text-1 italic truncate max-w-48">
						{{ list.folderName }}
					</span>
				</span>

				<ElDropdown
					v-if="list.canManageForm"
					class="flex justify-end w-100"
					:class="{ 'cursor-default': list.References.length === 0 }"
					trigger="click"
					placement="bottom-end"
					@visible-change="list.isContextMenuOpen = $event"
					@command="onContextMenuAction(list, $event)"
				>
					<CIconButton
						class="guest-access-list__parent-row--ellipsis text-icon-subtle"
						icon="ellipsis"
						:intense="false"
					/>
					<template #dropdown>
						<ElDropdownMenu>
							<ElDropdownItem>
								<RouterLink :to="list.buildLink" class="no-underline flex items-center gap-3">
									<Inline svg="build" class="text-icon-subtle" />
									<span class="text-primary">Build</span>
								</RouterLink>
							</ElDropdownItem>
							<ElDropdownItem v-if="list.isActive" command="toggle-edit-profile">
								<span class="flex items-center gap-3">
									<Inline svg="edit-profile" class="text-icon-subtle" />
									<span v-if="list.CanEditProfile">Disable profile editing</span>
									<span v-else>Allow guests to edit their profile</span>
								</span>
							</ElDropdownItem>
							<ElDropdownItem v-if="list.isActive" command="toggle-registration">
								<span class="flex items-center gap-3">
									<Inline svg="register" class="text-icon-subtle" />
									<span v-if="list.CanRegister">Disable registration</span>
									<span v-else>Allow guests to register</span>
								</span>
							</ElDropdownItem>
						</ElDropdownMenu>
					</template>
				</ElDropdown>
			</div>
			<CExpandable v-if="list.References?.length > 0" :expanded="list.isExpanded">
				<div v-for="formRef in list.References" :key="formRef.Id">
					<span class="guest-access-list__row guest-access-list__row--level-1 flex items-center gap-3 py-2">
						<Inline svg="form" class="text-icon-subtle ml-2" />
						<RouterLink :to="formRef.buildLink" class="truncate anchor--no-style">
							<span class="truncate" data-tooltip>{{ formRef.Name }}</span>
						</RouterLink>
						<CToolTip
							v-if="formRef.buildLink"
							placement="right"
							:use-button="false"
						>
							<template #button>
								<CIconButton
									class="guest-access-list__row--build text-icon-subtle"
									icon="build"
									:to="formRef.buildLink"
									:intense="false"
								/>
							</template>
							<span>Build Form</span>
						</CToolTip>
					</span>
					<div v-for="view in formRef.accessibleViews" :key="view.Id">
						<span class="guest-access-list__row guest-access-list__row--level-2 flex items-center gap-3 py-2">
							<Inline :svg="getViewIcon(view)" class="text-icon-subtle ml-2" />
							<span class="truncate">{{ view.Name }}</span>
						</span>
					</div>
				</div>
			</CExpandable>
		</div>
	</div>
</template>

<script lang="ts">
	import type { PropType } from 'vue';
	import { defineComponent, ref, computed, watch } from 'vue';
	import CCheckbox from 'src/components/form-controls/Checkbox.vue';
	import CBadge from 'src/components/info/Badge.vue';
	import CIconButton from 'src/components/interaction/IconButton.vue';
	import CToolTip from 'src/components/interaction/ToolTip.vue';
	import CExpandable from 'src/components/interaction/Expandable.vue';
	import { Dropdown, DropdownMenu, DropdownItem } from '@cognitoforms/element-ui';
	import { ViewType } from '@cognitoforms/types/server-types/forms/model/view-type';
	import type { GlobalStore } from 'src/stores/global-store';
	import type { GuestAccessViewModel } from '@cognitoforms/types/server-types/core/model/profile/guest-access-view-model';
	import type { GuestListViewModel } from '@cognitoforms/types/server-types/core/model/profile/guest-list-view-model';
	import type { GuestListReferenceViewModel } from '@cognitoforms/types/server-types/core/model/profile/guest-list-reference-view-model';
	import type { UserFormMeta } from '@cognitoforms/types/server-types/core/services/user-form-meta';
	import type { UserEntryViewMeta } from '@cognitoforms/types/server-types/core/services/user-entry-view-meta';
	import type { UpdateGuestListEvent, UpdateEditProfileEvent, UpdateRegistrationEvent } from './OrganizationGuestAccess.vue';
	import session from '@cognitoforms/api/services/session';

	interface IGuestAccessReference extends GuestListReferenceViewModel {
		accessibleViews: UserEntryViewMeta[];
		buildLink: string;
		icon: string;
	}

	export interface IGuestAccessList extends Omit<GuestListViewModel, 'References' | 'Enabled'> {
		References: IGuestAccessReference[];
		isActive: boolean;
		isExpanded: boolean;
		isDisabled: boolean;
		isContextMenuOpen: boolean;
		canManageForm: boolean;
		folderName: string;
		buildLink: string;
		icon: string;
	}

	export default defineComponent({
		name: 'GuestAccessList',
		components: {
			CIconButton,
			CCheckbox,
			CBadge,
			CToolTip,
			CExpandable,
			ElDropdown: Dropdown,
			ElDropdownMenu: DropdownMenu,
			ElDropdownItem: DropdownItem
		},
		props: {
			model: { type: Object as PropType<GuestAccessViewModel>, required: true },
			globalStore: { type: Object as PropType<GlobalStore>, required: true }
		},
		emits: [
			'guest-list-updated',
			'first-guest-list-enabled',
			'profile-editing-updated',
			'registration-updated',
			'show-guest-list-dialog'
		],
		setup(props, { emit }) {
			const allowAuthenticatedLinks = computed(() => props.model.GuestPortalEnabled);

			const findMatchingGuestListName = (lists: GuestListViewModel[] = []) => {
				const seen = new Set<string>();
				const duplicates = new Set<string>();

				lists.forEach(list => {
					const key = list.Name.trim().toLocaleLowerCase();
					if (seen.has(key))
						duplicates.add(key);
					else
						seen.add(key);
				});

				return [...duplicates];
			};

			const constructBuildLink = (form: UserFormMeta) => {
				if (form.CanManageForm)
					return `/${props.globalStore.organization.Code}/${form.UrlName}/build`;
			};

			const expandedNodes = ref([]);

			const buildReference = (refVm: GuestListReferenceViewModel, includeWithoutViews): IGuestAccessReference => {
				const refForm = props.globalStore.forms[refVm.Id];
				if (!refForm || refForm.IsArchived)
					return null;

				const accessibleViews = refForm.EntryViews.filter(view => {
					const isPublic = view.RoleId === refVm.PublicRoleId;
					const isQualifiedFormView = view.Type === ViewType.Form && refVm.HasGuestFormView;
					const isTableView = view.Type === ViewType.Table;
					return isPublic && (isQualifiedFormView || isTableView);
				});

				if (!includeWithoutViews && !accessibleViews.length)
					return null;

				return {
					...refVm,
					accessibleViews,
					buildLink: constructBuildLink(refForm as UserFormMeta),
					icon: 'form'
				};
			};

			const buildGuestList = (): IGuestAccessList[] => {
				const result: IGuestAccessList[] = [];

				const linkReferences = props.model.LinkGuestReferences;
				const linkGuestList: IGuestAccessList = {
					Id: 'link-guests',
					Name: 'Anyone sent a Save & Resume or Public Workflow link that requires authentication',
					References: linkReferences.map(r => buildReference(r, true)).filter(ref => ref !== null),
					ActiveGuests: null,
					isActive: props.model.GuestPortalEnabled,
					isDisabled: true,
					isExpanded: expandedNodes.value.includes('link-guests'),
					isContextMenuOpen: false,
					canManageForm: false,
					buildLink: null,
					folderName: undefined,
					icon: 'public-link',
					CanEditProfile: false,
					CanRegister: false
				};
				linkGuestList.References.sort((a, b) => a.Name.localeCompare(b.Name));

				const showFolderNames = findMatchingGuestListName(props.model.GuestLists);

				for (const list of props.model.GuestLists) {
					const form = props.globalStore.forms[list.Id];
					if (!form || form.IsArchived)
						continue;

					const refs: IGuestAccessReference[] = list.References
						.map(r => buildReference(r, false)).filter(ref => ref !== null);

					result.push({
						Id: list.Id,
						Name: list.Name,
						ActiveGuests: list.ActiveGuests,
						References: refs,
						isActive: list.Enabled && props.model.GuestPortalEnabled,
						isDisabled: !props.model.GuestPortalEnabled || !form.CanManageForm,
						isContextMenuOpen: false,
						canManageForm: form.CanManageForm,
						isExpanded: expandedNodes.value.includes(list.Id),
						buildLink: constructBuildLink(form as UserFormMeta),
						folderName: showFolderNames.includes(list.Name.toLocaleLowerCase()) ? props.globalStore.folders[form.FolderId]?.Name : null,
						icon: session.value?.enabledFeatures?.PersonSubmissions ? 'person-form' : 'person',
						CanEditProfile: list.CanEditProfile,
						CanRegister: list.CanRegister
					});
				}

				result.sort((a, b) => a.Name.localeCompare(b.Name));
				return [linkGuestList, ...result];
			};

			const expandList = (list: IGuestAccessList, event: MouseEvent) => {
				const isEllipsis = (event.target as HTMLElement).closest('.guest-access-list__parent-row--ellipsis');
				if (!list.References || !list.References.length || isEllipsis)
					return;

				list.isExpanded = !list.isExpanded;
				if (list.isExpanded)
					expandedNodes.value.push(list.Id);
				else
					expandedNodes.value = expandedNodes.value.filter(id => id !== list.Id);
			};

			const guestLists = ref(null);
			watch(() => props.model, () => {
				if (!props.model)
					return;

				guestLists.value = buildGuestList();
			}, { immediate: true, deep: true });

			const guestListChecked = (list: IGuestAccessList, enabled: boolean) => {
				if (enabled !== list.isActive) {
					if (enabled) {
						emit('show-guest-list-dialog', list);
					}
					else {
						emit('guest-list-updated', {
							formId: list.Id,
							enabled: enabled,
							canEditProfile: undefined,
							canRegister: undefined
						} as UpdateGuestListEvent);
					}
				}
			};

			const onGuestListCheckboxUserChange = (formId: string, enabled: boolean) => {
				const enabledCount = guestLists.value.filter(l => l.Id !== 'link-guests' && (l.Id === formId ? enabled : l.isActive)).length;
				if (enabled && enabledCount === 1)
					emit('first-guest-list-enabled');
			};

			const getViewIcon = (view: UserEntryViewMeta) => {
				if (view.Type === ViewType.Form)
					return 'form-view';
				else
					return view.IsTaskView ? 'task-grid' : 'grid';
			};

			const getBadgeText = (list: IGuestAccessList) => {
				const guestLabel = list.ActiveGuests === 1 ? 'guest' : 'guests';
				const formLabel = list.References.length === 1 ? 'form' : 'forms';

				if (list.ActiveGuests === null)
					return `${list.References.length} ${formLabel}`;
				else
					return `${list.ActiveGuests} ${guestLabel}, ${list.References.length} ${formLabel}`;
			};

			const onContextMenuAction = (list: IGuestAccessList, command: string) => {
				if (command === 'toggle-edit-profile') {
					emit('profile-editing-updated', {
						formId: list.Id,
						enabled: !list.CanEditProfile
					} as UpdateEditProfileEvent);
				}
				else if (command === 'toggle-registration') {
					emit('registration-updated', {
						formId: list.Id,
						enabled: !list.CanRegister
					} as UpdateRegistrationEvent);
				}
			};

			return {
				allowAuthenticatedLinks,
				guestLists,
				expandList,
				guestListChecked,
				onGuestListCheckboxUserChange,
				getViewIcon,
				getBadgeText,
				onContextMenuAction
			};
		}
	});
</script>

<style lang="scss">
$chevron-size: 21px;
$checkbox-size: 21px;
$sidebar-width: 280px;
$max-page-width: 762px;

// chevron-size + gap-2 + checkbox-size + gap-3 + ml-2 + size__icon
$level-1-margin: calc($chevron-size + $space__2 + $checkbox-size + $space__3 + $space__2 + $size__icon);

// level-1-margin + gap-3 + ml-2 + size__icon
$level-2-margin: calc($level-1-margin + $space__3 + $space__2 + $size__icon);

.guest-access-list {
	@include b-up($b7) {
		margin-left: -21px;
	}

	&__row {
		max-width: calc(100vw - $space__5 - $checkbox-size - $size__icon - $space__5 - $size__icon);
		height: 35px;

		@include b-up(700px) {
			max-width: calc(100vw - $sidebar-width - $space__5 - $checkbox-size - $size__icon - $space__5 - $size__icon);
		}

		@include b-up(1102px) {
			max-width: calc($max-page-width - $checkbox-size - $size__icon - $size__icon);
		}

		&--link-guest {
			max-width: calc(100vw - $space__5 - $checkbox-size - $size__icon);
			height: auto;

			@include b-up(700px) {
				max-width: calc(100vw - $sidebar-width - $space__5 - $checkbox-size - $size__icon);
			}
		}

		&--level-1 {
			margin-left: $level-1-margin;
		}

		&--level-2 {
			margin-left: $level-2-margin;
		}

		&--build {
			display: none !important;
		}

		a {
			color: var(--text-primary);
		}
	}

	&__parent-row {
		margin: 0 -5px;
		padding: 0 5px;

		.guest-access-list__chevron {
			visibility: hidden;
		}

		&--folder {
			display: block;
		}

		&--ellipsis {
			display: none !important;
		}

		&--static-hover {
			background: var(--bg-hovered);

			.guest-access-list__parent-row--ellipsis {
				background: var(--bg-hover-in-selected);
			}
		}

		&--link {

			.guest-access-list__chevron {
				margin-top: -$space__1;
			}
		}
	}

	.c-field {
		width: unset;
		float: none !important;
		margin-top: -4px;
	}

	.i-button {
		border-radius: $border-radius__text-button;
		padding: 2px;
	}

	.el-tooltip__popper {
		margin-left: 8px;
	}
}

.admin--desktop {

	.guest-access-list {

		&__row:hover {

			.guest-access-list__row--build {
				display: block !important;
			}

			.guest-access-list__parent-row--ellipsis {
				background: var(--bg-hover-in-selected);
			}
		}

		&__parent-row {

			&:hover,
			&--static-hover {
				background: var(--bg-hovered);

				.guest-access-list__chevron:not(.guest-access-list__chevron--hidden) {
					visibility: visible;
				}

				.guest-access-list__parent-row--folder {
					display: none;
				}

				.guest-access-list__parent-row--ellipsis {
					display: block !important;
				}
			}
		}

		.i-button:hover {
			background: var(--bg-hover-in-selected);
		}
	}
}

.admin--mobile {

	.guest-access-list {

		&__chevron:not(.guest-access-list__chevron--hidden) {
			visibility: visible;
		}

		&__parent-row {

			&--folder {
				display: none;
			}

			&--ellipsis {
				display: block !important;
			}
		}

		&__row--build {
			display: block !important;
		}
	}
}
</style>